import { queryActivityList } from "@/api/activity";
import { queryPreTag } from "@/api/home";
import { queryForcastShare } from "@/api/scode/share_card_info";
import { useCommonContext } from "@/utils/context/common";
import { countDown } from "@/utils/date-format";
import { shareCardByH5 } from "@/utils/share/share";
import Track from "@/utils/track";
import { getUrlParam } from "@/utils/url-parse";
import { API } from "@akc/biz-api";
import { TrackDataContext } from "@akc/mx-taro-h5-components";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// 倒计时Hook
const useCountdown = (activities: any[]) => {
  const [countDownList, setCountDownList] = useState<any[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const getCountDownObj = () => {
      const downTimeList = activities.map(({ beginTimeStr }) => +beginTimeStr);
      setCountDownList(countDown(downTimeList));
    };

    getCountDownObj();

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(getCountDownObj, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activities]);

  return countDownList;
};

// 排序选项配置
const SORT_OPTIONS_ADMIN = [
  { text: "综合", value: "1" },
  { text: "上新", value: "2", arrowType: 2 },
  { text: "转发", value: "4", arrowType: 2 },
];

const SORT_OPTIONS_C = [
  { text: "综合", value: "1" },
  { text: "上新", value: "2", arrowType: 2 },
];

const pageName = "预告";

const useForcast = () => {
  const track = Track();
  const navigate = useNavigate();

  const staticQuery = getUrlParam();
  const shopIdFromLocalStorage = localStorage.getItem("shopId");

  // 状态管理
  const [activeIndex, setActiveIndex] = useState(0);
  const [tabsArr, setTabsArr] = useState<any[]>([]);
  const [activityList, setActivityList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false); // 加载状态
  const [finished, setFinished] = useState(false); // 是否加载完成
  const sortObjRef = useRef({
    sortedType: 2,
    sortedModel: 1,
  });
  const [forwarded, setForwarded] = useState<boolean | null>(null);
  const [showHidden, setShowHidden] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [activeTabName, setActiveTabName] = useState("");
  const [showNormalPosterSwipe, setShowNormalPosterSwipe] = useState(false);
  const commonContext = useCommonContext();

  const setShowNormalPosterSwipeHandler = (show: boolean) => {
    setShowNormalPosterSwipe(show);
  };
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 埋点上下文数据设置
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { setTrackData } = useContext(TrackDataContext);
  // 分页参数
  const pageObj = useRef({
    pageNum: 1,
    pageSize: 10,
  });

  // 防重复请求
  const isFire = useRef(false);

  // 倒计时
  const countDownList = useCountdown(activityList);

  // 确保倒计时列表与活动列表长度一致
  useEffect(() => {
    if (activityList.length > 0 && countDownList.length !== activityList.length) {
      // 如果长度不一致，触发重新计算
      console.log("重新计算倒计时列表");
    }
  }, [activityList, countDownList]);

  // 计算属性
  const activeTab = tabsArr[activeIndex] || {};

  // 初始化标签页
  const initTabs = async () => {
    try {
      const shopId = staticQuery.shopId;
      const res = await queryPreTag(shopId);
      if (res) {
        const tabs = Array.isArray(res) ? res : [];
        setTabsArr(tabs);

        // 初始化活动列表
        if (tabs.length > 0) {
          pageObj.current.pageNum = 1;
          setActivityList([]);
          setFinished(false);
          await callQueryForcastActivity();
          setActiveTabName(tabs[0]?.activityTagName || "");
          tabExposeHandle(tabs[0]?.activityTagName, tabs?.length);
        }
      }
    } catch (error) {
      console.error("初始化标签页失败:", error);
    }
  };

  // 数据驱动的API请求函数
  const callQueryForcastActivity = useCallback(
    async (
      isFirstLoad?: string,
      customParams?: { showHidden?: boolean | null; forwarded?: boolean | null },
    ) => {
      if (tabsArr.length === 0) {
        setLoading(false);
        return;
      }

      if (isFire.current) {
        setLoading(false);
        return;
      }

      const { sortedModel, sortedType } = sortObjRef.current;

      // 使用自定义参数或当前状态参数
      const currentShowHidden =
        customParams?.showHidden !== undefined ? customParams.showHidden : showHidden;
      const currentForwarded =
        customParams?.forwarded !== undefined ? customParams.forwarded : forwarded;

      isFire.current = true;
      setLoading(true);

      try {
        const res = await queryActivityList({
          searchParams: {
            pageNum: pageObj.current.pageNum,
            pageSize: pageObj.current.pageSize,
            activityStatus: 1,
            activityTagNo: tabsArr[activeIndex]?.activityTagNo,
            sortModel: sortedModel as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
            sortType: sortedType as 1 | 2,
            ...(currentShowHidden !== null ? { seeHide: currentShowHidden } : {}),
            ...(currentForwarded !== null ? { isShare: currentForwarded } : {}),
          },
          voConfig: {
            showType: 2,
            withProductCnt: 3,
            withProductCount: 3,
          },
          pageId: "H5_ScheduleList",
          storeIdsClick: "",
        });

        setLoading(false);
        isFire.current = false;

        if (res) {
          const list = res?.result || [];

          if (res.pageIndex === 1) {
            setActivityList(list);
          } else {
            setActivityList(prev => [...prev, ...list]);
          }

          if (res.hasNext) {
            pageObj.current.pageNum = res.pageIndex + 1;
          } else {
            setFinished(true);
          }
          // 如果列表为空，也要更新状态
          if (list.length === 0 && res.pageIndex === 1) {
            setActivityList([]);
            setFinished(true);
          }
        } else {
          setFinished(true);
        }
      } catch (error) {
        console.error("查询活动列表失败:", error);
        setLoading(false);
        isFire.current = false;
        setFinished(true);
      }
    },
    [
      tabsArr,
      activeIndex,
      showHidden,
      forwarded,
      pageObj,
      isFire,
      sortObjRef,
      setLoading,
      setActivityList,
      setFinished,
    ],
  );

  // 标签页曝光处理
  const tabExposeHandle = (activityTagName?: string, nums?: number) => {
    track.track("tab_expose", {
      page_name: pageName,
      tab_name: activityTagName,
      nums: nums,
      rank: 1,
    });
  };

  // 处理标签切换
  const setActiveIndexHandler = (index: number) => {
    setActivityList([]);
    pageObj.current.pageNum = 1;
    isFire.current = false;
    setFinished(false);

    // 只更新索引，请求将在useEffect中处理
    setActiveIndex(index);
    setActiveTabName(tabsArr[index]?.activityTagName);

    // 将选中的标签滑动到屏幕中央
    setTimeout(() => {
      if (tabRefs.current[index]) {
        // 使用类似参考组件的方式滚动到视图中心
        tabRefs.current[index]?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }

      // 滚动到页面顶部
      if (scrollContainerRef.current) {
        // 滚动容器本身
        scrollContainerRef.current.scrollTop = 0;
      } else {
        // 否则滚动到窗口顶部
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 0);
    track.track("btn_click", {
      page_name: pageName,
      tab_name: tabsArr[activeIndex]?.activityTagName ?? "",
      nums: tabsArr?.length,
      rank: activeIndex + 1,
    });
  };

  // 当activeIndex改变时自动调用API
  const prevActiveIndexRef = useRef<number | null>(null);

  useEffect(() => {
    // 只有当activeIndex真正改变时才调用API
    if (tabsArr.length > 0 && tabsArr[activeIndex] && prevActiveIndexRef.current !== activeIndex) {
      // 在activeIndex更新后再调用API
      callQueryForcastActivity();
      prevActiveIndexRef.current = activeIndex; // 更新上一次的值
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, tabsArr]); // 不包含callQueryForcastActivity，避免无限循环

  // 下拉刷新
  const onRefresh = async () => {
    setTabsArr([]);
    setActiveIndex(0);
    setActivityList([]);
    pageObj.current.pageNum = 1;
    setLoading(false);
    setFinished(false);
    await initTabs();
  };

  // 上拉加载
  const onLoad = async () => {
    await callQueryForcastActivity();
  };

  // 处理排序
  const clickHandle = (obj: any) => {
    const { sortedModel, sortedType } = obj;

    // 对于上新(2)和转发(4)，实现点击切换排序方向
    if (sortedModel === 2 || sortedModel === 4) {
      // 如果是同一排序项被点击，则切换排序方向
      if (sortObjRef.current.sortedModel === sortedModel) {
        const newSortedType = sortObjRef.current.sortedType === 1 ? 2 : 1;
        const newSortObj = { sortedModel, sortedType: newSortedType };
        sortObjRef.current = newSortObj;
      } else {
        // 如果是切换到新的排序项，则使用默认的向下箭头
        const newSortObj = { sortedModel, sortedType: 2 };
        sortObjRef.current = newSortObj;
      }
    } else {
      // 对于综合(1)等其他选项，保持原有逻辑
      const newSortObj = { sortedModel, sortedType };
      sortObjRef.current = newSortObj;
    }

    track.track("btn_click", {
      page_name: pageName,
      btn_name: "排序",
      btn_text: sortedModel === 1 ? "综合" : sortedModel === 2 ? "上新" : "转发",
      btn_desc: sortedModel !== 1 && (sortObjRef.current.sortedType === 1 ? "升序" : "降序"),
    });

    setLoading(false);
    setFinished(false);
    pageObj.current.pageNum = 1;
    setActivityList([]); // 清空活动列表
    callQueryForcastActivity();
  };

  // 处理筛选
  const getScreenProduct = (data: any) => {
    console.log("筛选数据:", data);

    if (data.forwarded !== undefined) {
      // 已转发：每次点击都切换状态并请求接口
      const newValue = !forwarded; // 切换当前状态
      setForwarded(newValue);
      setFinished(false);
      pageObj.current.pageNum = 1;
      setActivityList([]);
      // 使用新的状态值立即调用API
      callQueryForcastActivity(undefined, { forwarded: newValue });
      track.track("btn_click", {
        page_name: pageName,
        module: "活动列表",
        btn_name: "活动筛选",
        btn_text: newValue ? "已转发" : "",
      });
    }

    if (data.showHidden !== undefined) {
      // 已隐藏/未隐藏：切换到目标状态并请求接口
      const boolValue = data.showHidden === "show"; // true代表"已隐藏"，false代表"未隐藏"

      // 计算新状态：如果当前状态与目标状态相同（已在选中状态），则取消选择（设为null）；否则切换到目标状态
      const newState = showHidden === boolValue ? null : boolValue;
      setShowHidden(newState);

      setFinished(false);
      pageObj.current.pageNum = 1;
      setActivityList([]);
      // 使用新的状态值立即调用API
      callQueryForcastActivity(undefined, { showHidden: newState });
      const currentShowHidden = newState !== undefined ? newState : showHidden;
      track.track("btn_click", {
        page_name: pageName,
        module: "活动列表",
        btn_name: "活动筛选",
        btn_desc: currentShowHidden !== null && (currentShowHidden ? "已隐藏" : "未隐藏"),
      });
    }
  };

  // 初始化
  // 获取预告分享信息
  const fetchForcastShareInfo = () => {
    const shopId = staticQuery.shopId;
    const distributorId = staticQuery.distributorId || getUrlParam().distributorId;

    if (shopId) {
      // 确保shopId存在才发起请求
      queryForcastShare({ shopId, distributorId })
        .then((res: any) => {
          // 根据Response类型处理响应
          if (res.success && res.data) {
            const { shopName, message, imgUrl, link } = res?.data || {};
            if (!imgUrl || !shopName || !message || !link) {
              return;
            }
            let confirmParam = {
              requestId: String(new Date().getTime()),
              scene: (res?.data && res?.data?.scode) || "",
              extraMap: {
                contentId: shopId,
                contentType: "1",
                activity_spu_code: "",
                sku_id: "",
              },
            };
            // 配置微信系统分享
            shareCardByH5(
              {
                title: shopName,
                desc: message,
                link: link,
                imgUrl: imgUrl,
              },
              confirmParam,
            );
          }
        })
        .catch(error => {
          console.error("获取预告分享信息失败:", error);
        });
    }
  };

  useEffect(() => {
    document.title = "精品预告";
    track.pageView(pageName, {
      page_name: pageName,
      domain: pageName,
    });

    initTabs();
    fetchForcastShareInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTrackData({
      page_name: pageName,
      tab_name: activeTabName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabName]);

  return {
    activeIndex,
    tabsArr,
    activityList,
    loading,
    finished,
    commonContext,
    forwarded,
    showHidden,
    open,
    showNormalPosterSwipe,
    setShowNormalPosterSwipe: setShowNormalPosterSwipeHandler,
    tabRefs,
    scrollContainerRef,
    countDownList,
    activeTab,
    sortObjRef,
    setActiveIndexHandler,
    onRefresh,
    onLoad,
    clickHandle,
    getScreenProduct,
    SORT_OPTIONS_ADMIN,
    SORT_OPTIONS_C,
    track,
    pageObj,
    navigate,
  };
};

export default useForcast;
